/**
 * shared/weapons/pm_weapon.ts — PM_Weapon, the weapon state machine run inside pmove (server AND client
 * prediction, so it must stay pure and deterministic: no trig here, only + - * / and integer ms timers).
 *
 * OWNER: WEAPONS agent.
 *
 * This is a port of the Black Ops 1 (T5) PM_Weapon family, tools/src/KisakBlack/src/bgame/bg_weapons.cpp
 * (PM_Weapon :1168, PM_Weapon_WeaponTimeAdjust :2653, PM_Weapon_CheckForChangeWeapon :2870, PM_BeginWeaponChange,
 * PM_Weapon_FinishWeaponChange :1617, PM_Weapon_CheckForReload :2348, PM_SetReloadingState, PM_Weapon_FireWeapon
 * :3198, PM_Weapon_StartFiring :3366, PM_Weapon_CheckForMelee :3742, PM_Weapon_MeleeInit :3806, PM_Weapon_OffHand*
 * :3851-4181, PM_Weapon_CheckForSprint :4592, PM_Weapon_CheckForDtp :4761, PM_UpdateAimDownSightFlag :472,
 * PM_UpdateAimDownSightLerp :693, PM_AdjustAimSpreadScale :967) and bg_pmove.cpp (PM_MeleeChargeStart :1488,
 * PM_CalcMeleeChargeTime :1575, PM_UpdatePlayerWalkingFlag :5165, PM_CmdScale_Walk weapon part :3017).
 * BO1 had the same crossbow / ballistic knife / tomahawk systems; using its engine logic for T6 is
 * ESTIMATE [WPN §0 "T5"]. All timings come from the weapon files via getWeaponDef() [WPN §2-§5].
 *
 * Mapping notes (T5 -> ours):
 *  - T5 weaponstate numbers differ from shared/types.ts WeaponState; names match. The offhand states keep T5's
 *    (confusing) transition order: INIT -> PREPARE (hold anim) -> START (waiting for the button release)
 *    -> HOLD (throw anim, fireDelay running) -> OFFHAND (thrown) -> END (primary quick raise).
 *  - T5 sets ps.weapon = 0 while an offhand is out; we keep ps.weapon = the primary (contract) and use
 *    ps.offHandIndex + the OFFHAND_* states. The blade-rotate hatchet's primary drop is instantaneous in T5
 *    (PM_BeginWeaponChange: offhand pending + WEAPROTATE_BLADE_ROTATE -> DROPPING_QUICK, weaponTime 0), so
 *    nothing is lost.
 *  - T5 cmd.weapon is the persistent "desired weapon"; ours is ps.weaponPending (set from cmd.weapon or the
 *    SWITCH_WEAPON press edge, cleared when the new weapon is raised).
 *  - First raise: T5 plays it the first time each weapon is raised in a life (PlayerHeldWeapon.heldBefore ->
 *    ps.weaponsHeldBefore). The brief also asks for a first raise of the spawn weapon (weaponSpawnRaise()).
 *  - T5 pm_flags 0x10 (sight aiming) = our PMF.UNK_0x10, T5 0x40 (ADS walk, x0.4 in CmdScale) = PMF.ADS_SLOW,
 *    T5 0x8000 sprinting = PMF.SPRINTING, T5 0x400000 dive = PMF.DIVE. pmove sets 0x10/0x40 before movement;
 *    the lunge movement (ps.meleeCharge*, set here at the swing) is pmove's (pmove/melee.ts).
 */
import { ENTITYNUM_NONE, PLAYER_MELEE_RANGE } from '../constants';
import { shortToAngle } from '../math';
import {
  Button, EF, PEv, PMF, PmType, WEAPANIM_TOGGLEBIT, WeapAnim, WeaponId, WeaponState,
  type PlayerState, type UserCmd,
} from '../types';
import type { PmoveContext } from '../pmove/types';
import type { WeaponDef } from './defs';
import { getWeaponDef } from './defs';
import type { PmWeaponHooks } from './types';

const S = WeaponState;
const ANIM_MASK = ~WEAPANIM_TOGGLEBIT & 0xffff;
/** PMF bits T5 uses for ADS (see header). */
const PMF_SIGHT_AIMING = PMF.UNK_0x10;
const PMF_ADS_WALK = PMF.ADS_SLOW;

/** Raise/drop kinds carried in the WEAPON_RAISE / WEAPON_DROP event parm (shared/types.ts PEv comments). */
export const RaiseKind = { NORMAL: 0, FIRST: 1, QUICK: 2, EMPTY: 3 } as const;

// =============================================================================================
// State classification helpers (T5 numeric ranges -> our names)
// =============================================================================================
export function isRaisingState(s: number): boolean {
  return s === S.RAISING || s === S.RAISING_ALTSWITCH || s === S.FIRST_RAISE;
}
export function isDroppingState(s: number): boolean {
  return s === S.DROPPING || s === S.DROPPING_QUICK;
}
/** T5 weaponstate 1..5. */
export function isSwitchingState(s: number): boolean {
  return isRaisingState(s) || isDroppingState(s);
}
export function isReloadingState(s: number): boolean {
  return s === S.RELOADING || s === S.RELOADING_INTERUPT || s === S.RELOAD_START || s === S.RELOAD_START_INTERUPT || s === S.RELOAD_END;
}
/** T5 6, 7, 31. */
export function isFiringState(s: number): boolean {
  return s === S.FIRING || s === S.RECHAMBERING;
}
/** T5 17..19. */
export function isMeleeState(s: number): boolean {
  return s === S.MELEE_INIT || s === S.MELEE_FIRE || s === S.MELEE_END;
}
/** T5 20..25. */
export function isOffhandState(s: number): boolean {
  return s >= S.OFFHAND_INIT && s <= S.OFFHAND_END;
}
/** T5 27..29. */
export function isSprintState(s: number): boolean {
  return s === S.SPRINT_RAISE || s === S.SPRINT_LOOP || s === S.SPRINT_DROP;
}
/** T5 40..42. */
export function isDtpState(s: number): boolean {
  return s === S.DTP_IN || s === S.DTP_LOOP || s === S.DTP_OUT;
}

function owns(ps: PlayerState, w: number): boolean {
  return w !== WeaponId.NONE && (ps.weaponsOwned & (1 << w)) !== 0;
}
function clipEmpty(ps: PlayerState): boolean {
  return ps.weapon !== WeaponId.NONE && ps.ammoClip[ps.weapon] === 0;
}

/** T5 PM_StartWeaponAnim: set the anim and flip the toggle bit so a restart of the same anim is visible. */
export function startWeaponAnim(ps: PlayerState, anim: number): void {
  ps.weaponAnim = (anim | ((ps.weaponAnim & WEAPANIM_TOGGLEBIT) ^ WEAPANIM_TOGGLEBIT)) & 0xffff;
}
/** T5 PM_ContinueWeaponAnim: restart only if a different anim is playing. */
function continueWeaponAnim(ps: PlayerState, anim: number): void {
  if ((ps.weaponAnim & ANIM_MASK) !== anim) startWeaponAnim(ps, anim);
}
function idleAnim(ps: PlayerState): number {
  return clipEmpty(ps) ? WeapAnim.EMPTY_IDLE : WeapAnim.IDLE;
}

/** T5 PM_Weapon_GetMeleeWeapDef: the held weapon if useAsMelee (ballistic knife) else ps.meleeWeapon (knife_mp) [WPN §5]. */
export function meleeWeaponOf(ps: PlayerState): WeaponId {
  if (ps.weapon !== WeaponId.NONE && getWeaponDef(ps.weapon).useAsMelee) return ps.weapon;
  return ps.meleeWeapon;
}

/**
 * Which weapon's viewmodel the first-person view shows (T5 BG_GetViewmodelWeaponIndex, bg_weapons.cpp:322):
 * the offhand (hatchet) during OFFHAND_PREPARE..OFFHAND, the melee weapon (knife_mp) during MELEE_INIT/FIRE when
 * the held weapon is not useAsMelee, otherwise ps.weapon. Viewmodel agent: play ps.weaponAnim on this weapon.
 */
export function viewmodelWeapon(ps: PlayerState): WeaponId {
  const s = ps.weaponState;
  if (ps.offHandIndex !== WeaponId.NONE && s >= S.OFFHAND_PREPARE && s <= S.OFFHAND) return ps.offHandIndex;
  if ((s === S.MELEE_INIT || s === S.MELEE_FIRE) && ps.weapon !== WeaponId.NONE && !getWeaponDef(ps.weapon).useAsMelee) return ps.meleeWeapon;
  return ps.weapon;
}

/** Desired weapon (T5 cmd.weapon): pending switch target, or the held weapon. NONE while on a ladder. */
function desiredWeapon(ps: PlayerState): WeaponId {
  if (ps.pm_flags & PMF.LADDER) return WeaponId.NONE;
  return ps.weaponPending !== WeaponId.NONE ? ps.weaponPending : ps.weapon;
}

/** Primaries in inventory order (crossbow, ballistic knife) [WPN §1]: SWITCH_WEAPON cycles through these. */
function isPrimary(w: number): boolean {
  return w !== WeaponId.NONE && getWeaponDef(w as WeaponId).inventoryType === 'primary';
}
function nextPrimary(ps: PlayerState, from: WeaponId): WeaponId {
  for (let i = 1; i <= 6; i++) {
    const w = ((from + i) % 6) as WeaponId;
    if (w !== WeaponId.NONE && owns(ps, w) && isPrimary(w)) return w;
  }
  return from;
}

// =============================================================================================
// Idle / spawn
// =============================================================================================
/** T5 PM_Weapon_Idle. */
function weaponIdle(ps: PlayerState): void {
  ps.weaponTime = 0;
  ps.weaponDelay = 0;
  ps.weaponState = S.READY;
  startWeaponAnim(ps, idleAnim(ps));
}

/**
 * Spawn raise (brief: "firstRaiseTime on spawn: WeaponState.FIRST_RAISE"). Call after the loadout is given
 * (combat.onPlayerSpawned does). Resets every per-life weapon timer; marks the spawn weapon as held.
 */
export function weaponSpawnRaise(ps: PlayerState, addEvent?: (ps: PlayerState, ev: PEv, parm: number) => void): void {
  const w = ps.weapon;
  ps.weaponPending = WeaponId.NONE;
  ps.offHandIndex = WeaponId.NONE;
  ps.weaponShotCount = 0;
  ps.weaponDelay = 0;
  ps.adsFrac = 0;
  ps.adsDelayTime = 0;
  ps.meleeChargeDist = 0;
  ps.meleeChargeYaw = 0;
  ps.meleeChargeTime = 0;
  ps.pm_flags &= ~(PMF_SIGHT_AIMING | PMF_ADS_WALK);
  if (w === WeaponId.NONE) { ps.weaponState = S.READY; ps.weaponTime = 0; return; }
  const def = getWeaponDef(w);
  ps.weaponsHeldBefore = (ps.weaponsHeldBefore | (1 << w)) & 0xff;
  ps.weaponState = S.FIRST_RAISE;
  ps.weaponTime = def.firstRaiseTimeMs;
  ps.aimSpreadScale = 255;
  startWeaponAnim(ps, WeapAnim.FIRST_RAISE);
  addEvent?.(ps, PEv.WEAPON_RAISE, w | (RaiseKind.FIRST << 8));
}

// =============================================================================================
// ADS (T5 PM_UpdateAimDownSightFlag / PM_UpdatePlayerWalkingFlag / PM_IsAdsAllowed / PM_UpdateAimDownSightLerp)
// =============================================================================================
/**
 * pm_flags 0x10 (sight aiming) and 0x40 (ADS walk) are set by pmove before its movement (pmove.ts
 * updateAimDownSightFlag / updatePlayerWalkingFlag, T5 PM_UpdateAimDownSightFlag order). PM_Weapon only mirrors
 * them: EF.ADS for remote players and ADS_UP / ADS_DOWN events on transitions (fly_generic_ads_plr /
 * fly_generic_ads_lower_plr [WPN §2.4]); EF.ADS holds the previous frame's value.
 */
function adsEvents(ps: PlayerState, addEvent: (ps: PlayerState, ev: PEv, parm: number) => void): void {
  const was = (ps.eFlags & EF.ADS) !== 0;
  const now = (ps.pm_flags & PMF_SIGHT_AIMING) !== 0;
  if (now) ps.eFlags |= EF.ADS; else ps.eFlags &= ~EF.ADS;
  if (now !== was) addEvent(ps, now ? PEv.ADS_UP : PEv.ADS_DOWN, ps.weapon);
}

/** T5 PM_UpdateAimDownSightLerp: adsFrac moves at 1/adsTransInTime up, 1/adsTransOutTime down [WPN §2.3: 250/250 ms]. */
function updateAimDownSightLerp(ps: PlayerState, msec: number): void {
  if (ps.weapon === WeaponId.NONE) { ps.adsFrac = 0; ps.adsDelayTime = 0; return; }
  const def = getWeaponDef(ps.weapon);
  if (!def.aimDownSight) { ps.adsFrac = 0; ps.adsDelayTime = 0; return; }
  let req = (ps.pm_flags & PMF_SIGHT_AIMING) !== 0;
  // no ADS during the reload until the last adsReloadTransTime (crossbow 600 ms) [WPN §2.3]
  if (!def.reloadWhileAds && ps.weaponState === S.RELOADING && ps.weaponTime - def.adsReloadTransTimeMs > 0) req = false;
  ps.adsDelayTime = 0; // player_adsExitDelay default 0 (T5 bg_misc.cpp:1010)
  if ((req && ps.adsFrac !== 1) || (!req && ps.adsFrac !== 0)) {
    const t = req ? def.adsTransInTimeMs : def.adsTransOutTimeMs;
    const rate = t > 0 ? (req ? 1 / t : -1 / t) : (req ? 1 : -1);
    let f = ps.adsFrac + msec * rate;
    if (f > 1) f = 1;
    if (f < 0) f = 0;
    ps.adsFrac = f;
  }
}

// =============================================================================================
// Spread (T5 PM_AdjustAimSpreadScale, PM_Weapon_AddFiringAimSpreadScale)
// =============================================================================================
/** bg_aimSpreadMoveSpeedThreshold default 11 (T5 bg_misc.cpp:934). ESTIMATE for T6. */
const AIM_SPREAD_MOVE_SPEED_THRESHOLD = 11;

function adjustAimSpreadScale(ps: PlayerState, cmd: Readonly<UserCmd>, msec: number): void {
  const def = getWeaponDef(ps.weapon);
  const frametime = msec * 0.001;
  let increase = 0;
  let decrease = 1;
  let rate = def.hipSpreadDecayRate;
  if (rate !== 0) {
    const onGround = ps.groundEntityNum !== ENTITYNUM_NONE || ps.pm_type === PmType.NORMAL_LINKED;
    if (onGround) {
      if (ps.pm_flags & PMF.PRONE) rate *= def.hipSpreadProneDecay;
      else if (ps.pm_flags & PMF.DUCKED) rate *= def.hipSpreadDuckedDecay;
    } else {
      rate *= 0.5;
    }
    decrease = rate * frametime;
    if (ps.adsFrac !== 1) {
      let viewchange = 0;
      // hipSpreadTurnAdd is 0 for every SnS weapon, so the turn term (needs the previous cmd angles) is omitted.
      if (def.hipSpreadMoveAdd !== 0 && (cmd.forwardmove !== 0 || cmd.rightmove !== 0)) {
        const sp2 = ps.velocity[0] * ps.velocity[0] + ps.velocity[1] * ps.velocity[1];
        if (sp2 > AIM_SPREAD_MOVE_SPEED_THRESHOLD * AIM_SPREAD_MOVE_SPEED_THRESHOLD && ps.speed > 0) {
          viewchange += (def.hipSpreadMoveAdd * Math.sqrt(sp2)) / ps.speed;
        }
      }
      if (!onGround) viewchange += 0.01 * 128 * 2; // T5: 2 x 0.01 x 128 per second in the air
      increase = viewchange * frametime;
    }
  } else {
    increase = 0;
    decrease = 1;
  }
  let a = increase > 0 ? ps.aimSpreadScale + increase * 255 : ps.aimSpreadScale - decrease * 255;
  if (a < 0) a = 0;
  if (a > 255) a = 255;
  ps.aimSpreadScale = a;
}

// =============================================================================================
// Weapon switching
// =============================================================================================
/** T5 BG_IsQuickSwitch. The ballistic knife (weaponClass pistol) always switches quick. */
function isQuickSwitch(oldW: WeaponId, newW: WeaponId, state: number): boolean {
  const oldDef = getWeaponDef(oldW);
  const newDef = getWeaponDef(newW);
  let quick = state === S.DROPPING_QUICK;
  if (oldW === WeaponId.NONE && newDef.weaponType === 'projectile') quick = true;
  if ((newW !== WeaponId.NONE && newDef.offhandSlot !== 'None') || (oldW !== WeaponId.NONE && oldDef.offhandSlot !== 'None')) quick = true;
  if (newW !== WeaponId.NONE && newDef.weaponClass === 'pistol') return true;
  return quick;
}

/** T5 PM_BeginWeaponChange. */
function beginWeaponChange(ps: PlayerState, newW: WeaponId, quick: boolean, ctx: PmoveContext): void {
  if (newW !== WeaponId.NONE && !owns(ps, newW)) return;
  if (isDroppingState(ps.weaponState)) return;
  ps.weaponDelay = 0;
  const oldW = ps.weapon;
  if (oldW !== WeaponId.NONE && owns(ps, oldW)) {
    const oldDef = getWeaponDef(oldW);
    const noAmmo = clipEmpty(ps);
    let kind: number = RaiseKind.NORMAL;
    if (noAmmo && (!quick || oldDef.noQuickDropWhenEmpty)) { startWeaponAnim(ps, WeapAnim.EMPTY_DROP); kind = RaiseKind.EMPTY; }
    else if (quick) { startWeaponAnim(ps, WeapAnim.QUICK_DROP); kind = RaiseKind.QUICK; }
    else startWeaponAnim(ps, WeapAnim.DROP);
    ctx.addEvent(ps, PEv.WEAPON_DROP, oldW | (kind << 8));
    ps.weaponState = quick ? S.DROPPING_QUICK : S.DROPPING;
    ps.weaponTime = quick ? oldDef.quickDropTimeMs : noAmmo ? oldDef.emptyDropTimeMs : oldDef.dropTimeMs;
  } else {
    ps.weaponTime = 0;
    ps.weaponState = quick ? S.DROPPING_QUICK : S.DROPPING;
  }
  if (newW !== WeaponId.NONE) ctx.addEvent(ps, PEv.WEAPON_SWITCH, newW);
}

/** T5 PM_Weapon_FinishWeaponChange (called when the drop timer ends). */
function finishWeaponChange(ps: PlayerState, prevState: number, ctx: PmoveContext): void {
  let newW = desiredWeapon(ps);
  if (!owns(ps, newW)) newW = WeaponId.NONE;
  const oldW = ps.weapon;
  ps.weapon = newW;
  // a ladder drop keeps the weapon to raise afterwards in weaponPending
  ps.weaponPending = (ps.pm_flags & PMF.LADDER) && oldW !== WeaponId.NONE ? oldW : WeaponId.NONE;
  if (oldW === newW || newW === WeaponId.NONE) {
    ps.weaponState = S.READY;
    ps.weaponTime = 0;
    startWeaponAnim(ps, newW === WeaponId.NONE ? WeapAnim.IDLE : idleAnim(ps));
    return;
  }
  const def = getWeaponDef(newW);
  const first = (ps.weaponsHeldBefore & (1 << newW)) === 0;
  ps.weaponsHeldBefore = (ps.weaponsHeldBefore | (1 << newW)) & 0xff;
  const quick = isQuickSwitch(oldW, newW, prevState);
  let anim: number, time: number, kind: number, state: WeaponState = S.RAISING;
  if (clipEmpty(ps)) { anim = WeapAnim.EMPTY_RAISE; time = def.emptyRaiseTimeMs; kind = RaiseKind.EMPTY; }
  else if (first) { anim = WeapAnim.FIRST_RAISE; time = def.firstRaiseTimeMs; kind = RaiseKind.FIRST; state = S.FIRST_RAISE; }
  else if (quick) { anim = WeapAnim.QUICK_RAISE; time = def.quickRaiseTimeMs; kind = RaiseKind.QUICK; }
  else { anim = WeapAnim.RAISE; time = def.raiseTimeMs; kind = RaiseKind.NORMAL; }
  // T5 PM_Weapon_BeginWeaponRaise
  ps.weaponState = state;
  ps.weaponTime = time;
  ps.weaponDelay = 0;
  ps.aimSpreadScale = 255;
  startWeaponAnim(ps, anim);
  // T5 adds EV_RAISE/EV_FIRST_RAISE only when an old weapon existed; we always announce the raise (audio). ESTIMATE.
  ctx.addEvent(ps, PEv.WEAPON_RAISE, newW | (kind << 8));
}

/** T5 PM_Weapon_CheckForChangeWeapon (melee/offhand/dtp states never switch; firing must finish first). */
function checkForChangeWeapon(ps: PlayerState, ctx: PmoveContext): void {
  const s = ps.weaponState;
  if (isMeleeState(s) || isOffhandState(s) || isDtpState(s)) return;
  const timeOk = ps.weaponTime === 0 || isReloadingState(s) || (!isFiringState(s) && ps.weaponDelay === 0);
  if (!timeOk) return;
  const desired = desiredWeapon(ps);
  if (ps.weapon === desired || (desired !== WeaponId.NONE && !owns(ps, desired))) {
    if (ps.weapon === desired && isDroppingState(s)) {
      // switched back while dropping: cancel the drop (T5 :2935)
      weaponIdle(ps);
    }
    if (desired !== WeaponId.NONE && !owns(ps, desired)) ps.weaponPending = WeaponId.NONE;
    return;
  }
  beginWeaponChange(ps, desired, desired === WeaponId.NONE ? true : isQuickSwitch(ps.weapon, desired, s), ctx);
}

// =============================================================================================
// Reload (T5 PM_Weapon_CheckForReload / PM_BeginWeaponReload / PM_SetReloadingState / PM_Weapon_FinishReload)
// =============================================================================================
function allowReload(ps: PlayerState): boolean {
  const w = ps.weapon;
  if (w === WeaponId.NONE) return false;
  return ps.ammoStock[w] > 0 && ps.ammoClip[w] < getWeaponDef(w).clipSize;
}

/** T5 PM_CheckForReloadInterferingWeaponStates: may an EMPTY clip auto-reload in this state? */
function autoReloadAllowed(ps: PlayerState, def: WeaponDef): boolean {
  const s = ps.weaponState;
  if (isSwitchingState(s) || isReloadingState(s) || isFiringState(s) || isSprintState(s)) return false;
  return !def.noADSAutoReload || ps.adsFrac !== 1;
}

function beginWeaponReload(ps: PlayerState, ctx: PmoveContext): void {
  const s = ps.weaponState;
  if (!(s === S.READY || isFiringState(s) || isSprintState(s) || isDtpState(s))) return;
  if (ps.weapon === WeaponId.NONE || (ps.pm_flags & PMF.MANTLE)) return;
  const w = ps.weapon;
  const def = getWeaponDef(w);
  ps.weaponShotCount = 0;
  // PM_SetReloadingState: weapType != bullet -> the "reload" anim + reloadTime even from empty (T5 :1976)
  startWeaponAnim(ps, WeapAnim.RELOAD);
  ps.weaponTime = def.reloadTimeMs;
  ps.weaponState = S.RELOADING;
  // PM_SetWeaponReloadAddAmmoDelay: ammo is credited at reloadAddTime (crossbow 1500 of 2850, knife 300 of 1100)
  const add = def.reloadAddTimeMs;
  ps.weaponDelay = add && add < def.reloadTimeMs ? add : def.reloadTimeMs;
  ctx.addEvent(ps, PEv.RELOAD_START, w);
}

function checkForReload(ps: PlayerState, cmd: Readonly<UserCmd>, frozen: boolean, ctx: PmoveContext): void {
  const s = ps.weaponState;
  if (ps.weapon === WeaponId.NONE) return;
  const def = getWeaponDef(ps.weapon);
  // knife swing ended with an empty crossbow: empty raise instead of the quick raise (T5 :2371)
  if (s === S.MELEE_END && clipEmpty(ps) && !def.useAsMelee) {
    ps.weaponState = S.RAISING;
    ps.weaponTime = def.emptyRaiseTimeMs;
    startWeaponAnim(ps, WeapAnim.EMPTY_RAISE);
    return;
  }
  if (isOffhandState(s) || isMeleeState(s) || isDtpState(s) || (ps.pm_flags & PMF.MANTLE)) return;
  let doReload = !frozen && (cmd.buttons & Button.RELOAD) !== 0 && allowReload(ps);
  if (ps.ammoClip[ps.weapon] === 0 && ps.ammoStock[ps.weapon] > 0 && autoReloadAllowed(ps, def)) doReload = true;
  if (doReload) beginWeaponReload(ps, ctx);
}

/** T5 PM_ReloadClip: stock -> clip. */
function reloadClip(ps: PlayerState, ctx: PmoveContext): void {
  const w = ps.weapon;
  const def = getWeaponDef(w);
  const n = Math.min(def.clipSize - ps.ammoClip[w], ps.ammoStock[w]);
  if (n > 0) {
    ps.ammoClip[w] += n;
    ps.ammoStock[w] -= n;
  }
  ctx.addEvent(ps, PEv.RELOAD_ADD_AMMO, w);
}

function finishReload(ps: PlayerState, delayed: boolean, ctx: PmoveContext): void {
  if (delayed) reloadClip(ps, ctx);
  if (ps.weaponTime === 0) {
    ps.weaponState = S.READY;
    startWeaponAnim(ps, idleAnim(ps));
    ctx.addEvent(ps, PEv.RELOAD_END, ps.weapon);
  }
}

// =============================================================================================
// Fire (T5 PM_Weapon_ShouldBeFiring / FireWeapon / StartFiring / CheckFiringAmmo / SetFPSFireAnim)
// =============================================================================================
function fireWeapon(ps: PlayerState, delayed: boolean, preFireSpread: number, ctx: PmoveContext): void {
  const w = ps.weapon;
  const def = getWeaponDef(w);
  if (!delayed && ps.ammoClip[w] < 1) {
    // PM_Weapon_CheckFiringAmmo
    if (ps.ammoStock[w] >= 1 && (!def.noADSAutoReload || ps.adsFrac !== 1)) {
      beginWeaponReload(ps, ctx);
    } else {
      ctx.addEvent(ps, PEv.WEAPON_DRYFIRE, w); // EV_NOAMMO: dry fire click
      continueWeaponAnim(ps, idleAnim(ps));
      ps.weaponTime += 500;
    }
    return;
  }
  // PM_Weapon_StartFiring
  const lastShot = ps.ammoClip[w] <= 1;
  ps.weaponTime = lastShot && def.lastFireTimeMs ? def.lastFireTimeMs : def.fireTimeMs; // crossbow 320 / 1152 [WPN §2.1]
  if (ps.weaponState !== S.FIRING) ps.weaponDelay = def.fireDelayMs;
  ps.weaponState = S.FIRING;
  if (ps.weaponDelay === 0 && def.fireType !== 'Full Auto') ps.weaponShotCount = Math.min(7, ps.weaponShotCount + 1);
  if (ps.weaponDelay !== 0) return; // fires when the delay runs out (delayedAction)
  // the shot
  ps.fireSpreadScale = Math.round(preFireSpread) & 0xff;
  ps.ammoClip[w] -= 1;
  const empty = ps.ammoClip[w] === 0;
  if (ps.adsFrac > 0.75) startWeaponAnim(ps, empty ? WeapAnim.ADS_LAST_SHOT : WeapAnim.ADS_FIRE);
  else startWeaponAnim(ps, empty ? WeapAnim.LAST_SHOT : WeapAnim.FIRE);
  ps.fireSeq = (ps.fireSeq + 1) & 0xffff;
  ctx.addEvent(ps, empty ? PEv.WEAPON_FIRE_LAST : PEv.WEAPON_FIRE, w);
  // PM_Weapon_AddFiringAimSpreadScale: hipSpreadFireAdd (crossbow 1.0 -> full spread) unless fully ADS
  if (ps.adsFrac !== 1) ps.aimSpreadScale = Math.min(255, ps.aimSpreadScale + def.hipSpreadFireAdd * 255);
  // BG_SwitchWeaponsIfEmpty
  if (empty && ps.ammoStock[w] === 0) ctx.addEvent(ps, PEv.NOAMMO, w);
}

// =============================================================================================
// Melee (T5 PM_Weapon_CheckForMelee / PM_MeleeChargeStart / PM_Weapon_MeleeInit / MeleeFire / MeleeEnd)
// =============================================================================================
function meleeChargeClear(ps: PlayerState): void {
  ps.meleeChargeYaw = 0;
  ps.meleeChargeDist = 0;
  ps.meleeChargeTime = 0;
}

function checkForMelee(ps: PlayerState, cmd: Readonly<UserCmd>, delayed: boolean, frozen: boolean, ctx: PmoveContext): void {
  if (frozen || (ps.pm_flags & (PMF.LADDER | PMF.MANTLE))) return;
  if (ps.weapon === WeaponId.NONE) return;
  const s = ps.weaponState;
  if (isMeleeState(s) || isOffhandState(s) || isDtpState(s) || isSwitchingState(s)) return;
  if (delayed) return;
  if (ps.weaponDelay !== 0 && !isReloadingState(s)) return;
  if (!(cmd.buttons & Button.MELEE) || (ps.oldButtons & Button.MELEE)) return;
  const primary = getWeaponDef(ps.weapon);
  if (ps.adsFrac > 0 && primary.adsOverlayReticle !== 'none') return;
  const mw = meleeWeaponOf(ps);
  const mdef = getWeaponDef(mw);
  if (mdef.meleeDamage <= 0) return;
  // PM_MeleeChargeStart: the lunge target comes from the auto-melee assist (cmd.meleeChargeDist) [CTL §5.5]
  const onGround = ps.groundEntityNum !== ENTITYNUM_NONE;
  if (ps.meleeChargeDist === 0 && cmd.meleeChargeDist > 0 && ps.pm_type === PmType.NORMAL && onGround) {
    ps.meleeChargeYaw = shortToAngle(cmd.meleeChargeYaw & 0xffff);
    ps.meleeChargeDist = Math.min(255, cmd.meleeChargeDist | 0);
    ps.meleeChargeTime = 0;
  } else {
    meleeChargeClear(ps);
  }
  // PM_Weapon_MeleeInit: lunge when the charge distance exceeds player_meleeRange 64 [WPN §5]. NOTE: 64 and the
  // assist's dist_offset 32 are T5 (bg_misc.cpp:1521, aim_assist.cpp:1890); the T6 range cap is aim_automelee_range
  // 100 (dvars_t6mp.json). Together a knife_mp lunge needs a target 96-100 u away (ballistic knife: 96-116). T6's
  // player_meleeRange default is registered through a concealed call in t6mp_offline.exe and is not recovered, so
  // the window is left as cited rather than tuned. Short-range melee still pulls you in (pmove/melee.ts charge).
  const lunge = ps.meleeChargeDist > PLAYER_MELEE_RANGE;
  if (lunge) {
    ps.weaponTime = mdef.meleeChargeTimeMs;
    ps.weaponDelay = mdef.meleeChargeDelayMs;
    startWeaponAnim(ps, WeapAnim.MELEE_CHARGE);
  } else {
    ps.weaponTime = mdef.meleeTimeMs;
    ps.weaponDelay = mdef.meleeDelayMs;
    startWeaponAnim(ps, WeapAnim.MELEE);
  }
  ps.weaponState = S.MELEE_INIT;
  ctx.addEvent(ps, lunge ? PEv.MELEE_LUNGE : PEv.MELEE_SWING, mw);
}

function meleeEnd(ps: PlayerState): void {
  const def = getWeaponDef(ps.weapon);
  if (def.useAsMelee || ps.weapon === WeaponId.NONE) {
    weaponIdle(ps);
  } else {
    // knife_mp swing with the crossbow out: crossbow quickRaise (0.5 s) [WPN §5 T5 PM_Weapon_MeleeEnd]
    ps.weaponState = S.MELEE_END;
    ps.weaponTime = def.quickRaiseTimeMs;
    ps.weaponDelay = 0;
    startWeaponAnim(ps, WeapAnim.QUICK_RAISE);
  }
}

// =============================================================================================
// Offhand: the hatchet (T5 PM_Weapon_CheckForOffHand / OffHandInit / Prepare / Hold / Start / OffHand / End)
// =============================================================================================
/** clipOnly offhands (hatchet_mp clipOnly 1) only use the clip: SnS gives clip 1 + stock 1 = ONE axe [SAS §2]. */
function offhandAmmo(ps: PlayerState, w: WeaponId): number {
  return getWeaponDef(w).clipOnly ? ps.ammoClip[w] : ps.ammoClip[w] + ps.ammoStock[w];
}

function checkForOffHand(ps: PlayerState, cmd: Readonly<UserCmd>, frozen: boolean, ctx: PmoveContext): boolean {
  if (!(cmd.buttons & Button.FRAG) || (ps.oldButtons & Button.FRAG)) return false;
  if (frozen || (ps.pm_flags & (PMF.SPRINTING | PMF.LADDER | PMF.MANTLE))) return false;
  const s = ps.weaponState;
  if (isDroppingState(s) || (isOffhandState(s) && s !== S.OFFHAND_END) || isDtpState(s)) return false;
  const off = ps.offhandPrimary;
  if (off === WeaponId.NONE || !owns(ps, off) || offhandAmmo(ps, off) <= 0) {
    ctx.addEvent(ps, PEv.NOAMMO, off === WeaponId.NONE ? WeaponId.HATCHET : off); // EV_EMPTY_OFFHAND
    return false;
  }
  ps.offHandIndex = off;
  if (s === S.OFFHAND_END) {
    offHandPrepare(ps, ctx);
    return false;
  }
  // PM_Weapon_OffHandInit (+ the instantaneous blade-rotate primary drop, T5 PM_BeginWeaponChange)
  ps.weaponState = S.OFFHAND_INIT;
  ps.weaponTime = 0;
  ps.weaponDelay = 0;
  ps.weaponShotCount = 0;
  return true; // T5 needs one more frame before OffHandPrepare
}

function offHandPrepare(ps: PlayerState, ctx: PmoveContext): void {
  const def = getWeaponDef(ps.offHandIndex);
  ps.weaponState = S.OFFHAND_PREPARE;
  ps.weaponTime = def.holdFireTimeMs; //    hatchet holdFireTime 0 [WPN §4]
  ps.weaponDelay = 0;
  ctx.addEvent(ps, PEv.OFFHAND_START, ps.offHandIndex); // EV_PREP_OFFHAND
  startWeaponAnim(ps, WeapAnim.HOLD_FIRE);
}

/** T5 PM_Weapon_OffHandEnd: quick raise of the primary (crossbow 0.5 s) or its empty raise. */
function offHandEnd(ps: PlayerState, ctx: PmoveContext): void {
  if (ps.weapon !== WeaponId.NONE) {
    const def = getWeaponDef(ps.weapon);
    ps.weaponTime = def.quickRaiseTimeMs;
    startWeaponAnim(ps, clipEmpty(ps) ? WeapAnim.EMPTY_RAISE : WeapAnim.QUICK_RAISE);
  } else {
    ps.weaponTime = 0;
  }
  ps.weaponDelay = 0;
  ps.weaponState = S.OFFHAND_END;
  ctx.addEvent(ps, PEv.OFFHAND_END, ps.offHandIndex);
}

/** Cancel = the weapon-cycle press while an offhand is held (T5 CG_CancelOffhand via WeaponCycleAllowed). */
function cancelPressed(ps: PlayerState, cmd: Readonly<UserCmd>): boolean {
  return (cmd.buttons & Button.SWITCH_WEAPON) !== 0 && (ps.oldButtons & Button.SWITCH_WEAPON) === 0;
}

function offHandStart(ps: PlayerState, cmd: Readonly<UserCmd>, ctx: PmoveContext): void {
  const def = getWeaponDef(ps.offHandIndex);
  const held = (cmd.buttons & Button.FRAG) !== 0 && (ps.oldButtons & Button.FRAG) !== 0;
  if (!def.holdButtonToThrow && held) {
    if (def.offhandHoldIsCancelable && cancelPressed(ps, cmd)) { offHandEnd(ps, ctx); return; }
    ps.weaponDelay = 1; // keep holding: re-check next frame
    return;
  }
  // throw: fireTime 350 anim, the axe leaves at fireDelay 48 after THIS point (the throw start) [WPN §4]. From the
  // button press it is ~98 ms at 60 Hz: T5 stages OFFHAND_INIT -> PREPARE -> START one frame each before the throw
  // (bg_weapons.cpp:1385-1396, 3851-3917), so the extra ~50 ms is authentic.
  ps.weaponState = S.OFFHAND_HOLD;
  ps.weaponTime = def.fireTimeMs;
  ps.weaponDelay = def.fireDelayMs;
  startWeaponAnim(ps, WeapAnim.FIRE);
  if (ps.weaponDelay === 0) offHandThrow(ps, ctx);
}

function offHandThrow(ps: PlayerState, ctx: PmoveContext): void {
  const off = ps.offHandIndex;
  if (offhandAmmo(ps, off) > 0) {
    if (ps.ammoClip[off] > 0) ps.ammoClip[off] -= 1; else ps.ammoStock[off] -= 1;
    ps.fireSeq = (ps.fireSeq + 1) & 0xffff;
    ctx.addEvent(ps, PEv.OFFHAND_THROW, off); // EV_USE_OFFHAND: the server spawns the hatchet
  } else {
    ctx.addEvent(ps, PEv.NOAMMO, off);
  }
  ps.weaponState = S.OFFHAND;
}

// =============================================================================================
// Sprint / dive-to-prone weapon states (T5 PM_Weapon_CheckForSprint / CheckForDtp)
// =============================================================================================
function checkForSprint(ps: PlayerState): void {
  if (ps.weapon === WeaponId.NONE) return;
  const s = ps.weaponState;
  if (isFiringState(s) || isMeleeState(s) || isSwitchingState(s) || isOffhandState(s) || isDtpState(s)) return;
  if (ps.pm_flags & PMF.DIVE) return;
  const def = getWeaponDef(ps.weapon);
  const empty = clipEmpty(ps);
  if ((ps.pm_flags & PMF.SPRINTING) && !isSprintState(s)) {
    ps.weaponState = S.SPRINT_RAISE;
    ps.weaponTime = def.sprintInTimeMs;
    ps.weaponDelay = 0;
    startWeaponAnim(ps, empty ? WeapAnim.SPRINT_IN_EMPTY : WeapAnim.SPRINT_IN);
  } else if (!(ps.pm_flags & PMF.SPRINTING) && (s === S.SPRINT_RAISE || s === S.SPRINT_LOOP)) {
    ps.weaponState = S.SPRINT_DROP;
    ps.weaponTime = def.sprintOutTimeMs;
    ps.weaponDelay = 0;
    startWeaponAnim(ps, empty ? WeapAnim.SPRINT_OUT_EMPTY : WeapAnim.SPRINT_OUT);
  }
}

function checkForDtp(ps: PlayerState): void {
  if (ps.weapon === WeaponId.NONE) return;
  const s = ps.weaponState;
  if (isFiringState(s) || isMeleeState(s) || isSwitchingState(s) || isOffhandState(s) || isDtpState(s)) return;
  if (!(ps.pm_flags & PMF.DIVE)) return;
  const def = getWeaponDef(ps.weapon);
  ps.weaponState = S.DTP_IN;
  ps.weaponTime = def.dtpInTimeMs;
  ps.weaponDelay = 0;
  continueWeaponAnim(ps, WeapAnim.DTP_IN);
}

// =============================================================================================
// Timers (T5 PM_Weapon_WeaponTimeAdjust, no perks in SnS)
// =============================================================================================
function weaponTimeAdjust(ps: PlayerState, cmd: Readonly<UserCmd>, msec: number): boolean {
  if (ps.weaponTime) {
    ps.weaponTime -= msec;
    if (ps.weaponTime <= 0) {
      const def = getWeaponDef(ps.weapon);
      const shotLimit = def.singleShot && ps.weaponShotCount >= 1; // ShotLimitReached
      const holding = (cmd.buttons & Button.ATTACK) !== 0;
      if (!isOffhandState(ps.weaponState) && shotLimit && holding && ps.weapon === desiredWeapon(ps) && ps.weapon !== WeaponId.NONE && ps.ammoClip[ps.weapon] > 0) {
        // single shot held through the fire time: park at weaponTime 1 until the trigger is released
        ps.weaponTime = 1;
        if (isReloadingState(ps.weaponState)) { ps.weaponTime = 0; ps.weaponShotCount = 0; }
        else if (isFiringState(ps.weaponState) || isMeleeState(ps.weaponState)) {
          continueWeaponAnim(ps, idleAnim(ps));
          ps.weaponState = S.READY;
        }
      } else {
        if (!holding) ps.weaponShotCount = 0;
        ps.weaponTime = 0;
      }
    }
  }
  if (!ps.weaponDelay) return false;
  ps.weaponDelay -= msec;
  if (ps.weaponDelay > 0) return false;
  ps.weaponDelay = 0;
  return true;
}

// =============================================================================================
// PM_Weapon
// =============================================================================================
/** Input side: cmd.weapon request and the SWITCH_WEAPON press edge (cycle primaries) set the desired weapon. */
function updateDesiredWeapon(ps: PlayerState, cmd: Readonly<UserCmd>): void {
  const s = ps.weaponState;
  if (cmd.weapon !== WeaponId.NONE && owns(ps, cmd.weapon) && isPrimary(cmd.weapon)) {
    ps.weaponPending = cmd.weapon === ps.weapon ? WeaponId.NONE : cmd.weapon;
  }
  const press = (cmd.buttons & Button.SWITCH_WEAPON) !== 0 && (ps.oldButtons & Button.SWITCH_WEAPON) === 0;
  if (!press) return;
  // T5 WeaponCycleAllowed: no cycling while mantling / diving; during a cancelable offhand the press cancels it.
  if (isOffhandState(s) || (ps.pm_flags & (PMF.MANTLE | PMF.DIVE))) return;
  const next = nextPrimary(ps, desiredWeapon(ps) || ps.weapon);
  ps.weaponPending = next === ps.weapon ? WeaponId.NONE : next;
}

function step(ps: PlayerState, cmd: Readonly<UserCmd>, msec: number, ctx: PmoveContext): void {
  const t = ps.pm_type;
  if (t === PmType.DEAD || t === PmType.DEAD_LINKED || t === PmType.SPECTATOR || t === PmType.INTERMISSION || t === PmType.NOCLIP || t === PmType.UFO) return;
  // pre-match / post-game: timers run (the raise finishes), but no fire/melee/offhand/reload [SAS §1, gametype #G3]
  const frozen = t === PmType.FREEZE || (ps.pm_flags & PMF.FROZEN) !== 0;
  const preFireSpread = ps.aimSpreadScale; // T5 FireWeapon uses the previous frame's spread (g_active_mp.cpp:2489)

  if (ps.weapon !== WeaponId.NONE) adjustAimSpreadScale(ps, cmd, msec);
  updateDesiredWeapon(ps, cmd);
  adsEvents(ps, ctx.addEvent);
  updateAimDownSightLerp(ps, msec);

  const delayed = weaponTimeAdjust(ps, cmd, msec);
  checkForSprint(ps);
  checkForDtp(ps);
  const offhandInit = checkForOffHand(ps, cmd, frozen, ctx);
  checkForChangeWeapon(ps, ctx);
  checkForReload(ps, cmd, frozen, ctx);
  checkForMelee(ps, cmd, delayed, frozen, ctx);
  // PM_Weapon_CheckForGrenadeThrowCancel (INIT / PREPARE)
  if ((ps.weaponState === S.OFFHAND_INIT || ps.weaponState === S.OFFHAND_PREPARE) && ps.offHandIndex !== WeaponId.NONE
    && getWeaponDef(ps.offHandIndex).offhandHoldIsCancelable && cancelPressed(ps, cmd)) {
    offHandEnd(ps, ctx);
  }
  // full spread while prone-moving or swinging (T5 PM_Weapon :5404)
  if (((ps.pm_flags & PMF.PRONE) && (cmd.forwardmove || cmd.rightmove) && ps.adsFrac !== 1) || isMeleeState(ps.weaponState)) {
    ps.aimSpreadScale = 255;
  }
  // T5 eFlags 0x40 "firing" (bg_pmove.cpp:1809): attack held in READY/FIRING with ammo, or the fire time running
  ps.eFlags &= ~EF.FIRING;
  if ((ps.weaponState === S.READY || ps.weaponState === S.FIRING) && ps.weapon !== WeaponId.NONE && ps.ammoClip[ps.weapon] > 0
    && ((cmd.buttons & Button.ATTACK) || ps.weaponTime > 0)) ps.eFlags |= EF.FIRING;

  if (offhandInit) return;
  if (!delayed && (ps.weaponTime || ps.weaponDelay)) return;

  switch (ps.weaponState) {
    case S.RAISING:
    case S.RAISING_ALTSWITCH:
    case S.FIRST_RAISE:
      ps.weaponState = S.READY;
      startWeaponAnim(ps, idleAnim(ps));
      return;
    case S.DROPPING:
    case S.DROPPING_QUICK:
      finishWeaponChange(ps, ps.weaponState, ctx);
      return;
    case S.RELOADING:
    case S.RELOADING_INTERUPT:
      finishReload(ps, delayed, ctx);
      return;
    case S.RELOAD_START:
    case S.RELOAD_START_INTERUPT:
    case S.RELOAD_END:
      weaponIdle(ps); // segmented reloads are not used by any SnS weapon
      return;
    case S.MELEE_INIT:
      ps.weaponState = S.MELEE_FIRE;
      ctx.addEvent(ps, PEv.MELEE_STRIKE, meleeWeaponOf(ps)); // EV_FIRE_MELEE: the server traces now
      return;
    case S.MELEE_FIRE:
      meleeEnd(ps);
      return;
    case S.MELEE_END:
    case S.SPRINT_DROP:
    case S.DTP_OUT:
      weaponIdle(ps);
      return;
    case S.OFFHAND_INIT:
      offHandPrepare(ps, ctx);
      return;
    case S.OFFHAND_PREPARE:
      // T5 PM_Weapon_OffHandHold
      ps.weaponState = S.OFFHAND_START;
      ps.weaponTime = 0;
      ps.weaponDelay = 0;
      return;
    case S.OFFHAND_START:
      offHandStart(ps, cmd, ctx);
      return;
    case S.OFFHAND_HOLD:
      offHandThrow(ps, ctx);
      return;
    case S.OFFHAND:
      offHandEnd(ps, ctx);
      return;
    case S.OFFHAND_END:
      weaponIdle(ps);
      ps.offHandIndex = WeaponId.NONE;
      return;
    case S.SPRINT_RAISE: {
      const e = clipEmpty(ps);
      ps.weaponState = S.SPRINT_LOOP;
      ps.weaponTime = 0;
      ps.weaponDelay = 0;
      startWeaponAnim(ps, e ? WeapAnim.SPRINT_LOOP_EMPTY : WeapAnim.SPRINT_LOOP);
      return;
    }
    case S.SPRINT_LOOP:
      return;
    case S.DTP_IN:
      ps.weaponState = S.DTP_LOOP;
      ps.weaponTime = 0;
      ps.weaponDelay = 0;
      continueWeaponAnim(ps, WeapAnim.DTP_LOOP);
      return;
    case S.DTP_LOOP:
      if (!(ps.pm_flags & PMF.DIVE)) {
        ps.weaponState = S.DTP_OUT;
        ps.weaponTime = getWeaponDef(ps.weapon).dtpOutTimeMs;
        ps.weaponDelay = 0;
        continueWeaponAnim(ps, WeapAnim.DTP_OUT);
      }
      return;
    default:
      break;
  }
  // READY / FIRING: PM_Weapon_ShouldBeFiring
  if (ps.weapon === WeaponId.NONE) return;
  if (!(cmd.buttons & Button.ATTACK) && !delayed) {
    if (ps.weaponState === S.FIRING) continueWeaponAnim(ps, idleAnim(ps));
    ps.weaponState = S.READY;
    return;
  }
  if (frozen || (ps.pm_flags & PMF.MANTLE)) return;
  fireWeapon(ps, delayed, preFireSpread, ctx);
}

/**
 * Weapon part of PM_CmdScale_Walk (T5 bg_pmove.cpp:3017): ADS walk (PMF.ADS_SLOW) uses adsMoveSpeedScale,
 * otherwise the weapon's moveSpeedScale (ballistic knife 1.02, crossbow 1.0) [WPN §2.3, §3]. The x0.4 ADS factor
 * itself is pmove's (PM §3). T6's 0x940730 "ADS-lerped" variant is unread; for SnS both give the same values.
 */
function moveSpeedScale(ps: PlayerState): number {
  if (ps.weapon === WeaponId.NONE) return 1;
  const def = getWeaponDef(ps.weapon);
  if (def.moveSpeedScale <= 0 || (ps.pm_flags & PMF_ADS_WALK)) return def.adsMoveSpeedScale > 0 ? def.adsMoveSpeedScale : 1;
  return def.moveSpeedScale;
}

/** T5 PM_SprintEndingButtons / PM_ShouldEndSprint weapon part: melee and offhand states end (and block) sprint. */
function blocksSprint(ps: PlayerState): boolean {
  return isMeleeState(ps.weaponState) || isOffhandState(ps.weaponState);
}

export const pmWeaponHooks: PmWeaponHooks = { step, moveSpeedScale, blocksSprint };
